import {
  POI_STATUS_COLOR_MAP,
  POI_INSPECTION_PRIORITY_COLOR_MAP,
  POI_DUE_DATE_COLOR_MAP,
  abyssColors,
  PD_ALARM_LEVEL_COLOR_MAP,
} from '@/theme/colors';
import { Accordion, Box, Skeleton, Stack, Typography, styled as muiStyled } from '@mui/material';

type BarProps = {
  background: string;
  offset: number;
  width: string;
};

type WidgetProps = {
  loading?: boolean;
};

export const SummaryTitle = muiStyled(Typography)({
  fontSize: '1.6rem',
  fontWeight: 500,
  color: abyssColors.primary[500],
  lineHeight: '24px',
  margin: '0px !important',
});

export const SummaryAccordion = muiStyled(Accordion)(() => ({
  borderRadius: '0.4rem',
  border: `0.1rem solid ${abyssColors.primary[200]}`,
  backgroundColor: abyssColors.primary[50],
  padding: '1.6rem 2.4rem 1.6rem 1.6rem',
  margin: '3.2rem 0 !important',
  '&.MuiAccordion-root::before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-content': {
    margin: '0px !important',
  },
}));

export const Widget = muiStyled(Stack)(({ loading }: WidgetProps) => ({
  padding: '1.6rem',
  flex: '1 0',
  height: '25.3rem',
  borderRadius: loading ? '0.6rem' : '0.8rem',
  border: loading ? 'none' : `0.1rem solid ${abyssColors.primary[100]}`,
  background: loading ? 'rgba(241, 239, 239, 0.40)' : abyssColors.neutral[50],
  flexDirection: 'column',
  gap: '1.6rem',
  transition: 'background ease 0.3s',
  '& .MuiSkeleton-root::after': {
    width: '150%',
    background: 'linear-gradient(90deg, transparent, rgba(217, 217, 217, 0.5), transparent)',
  },
}));

export const Bar = muiStyled(Box)(({ background, offset, width }: BarProps) => ({
  backgroundColor: background,
  height: '1.2rem',
  width: width,
  display: 'inline-block',
  border: '0.1rem solid rgba(0, 0, 0, 0.20)',
  borderRadius: '0.2rem',
  position: 'absolute',
  left: `${offset * 100}%`,
}));

export const CustomSkeleton = muiStyled(Skeleton)(() => ({
  background: 'transparent',
  transform: 'unset',
  height: '1.5rem',
}));

export const getStatusColors = (key: string) => {
  switch (key) {
    case 'In Review':
      return POI_STATUS_COLOR_MAP.INREVIEW;
    case 'In Progress':
      return POI_STATUS_COLOR_MAP.INPROGRESS;
    case 'Completed':
      return POI_STATUS_COLOR_MAP.COMPLETED;
    case 'Remediated':
      return POI_STATUS_COLOR_MAP.REMEDIATED;
    case 'Chase Item Added':
      return POI_STATUS_COLOR_MAP.CHASE_ITEM_ADDED;
    default:
      return abyssColors.neutral[100];
  }
};

export const getPriorityColors = (key: string) => {
  switch (key) {
    case 'High':
      return POI_INSPECTION_PRIORITY_COLOR_MAP.HIGH;
    case 'Medium':
      return POI_INSPECTION_PRIORITY_COLOR_MAP.MEDIUM;
    case 'Low':
      return POI_INSPECTION_PRIORITY_COLOR_MAP.LOW;
    default:
      return '#E6E6E6';
  }
};

export const getDueDateColors = (key: string) => {
  switch (key) {
    case 'Overdue':
      return POI_DUE_DATE_COLOR_MAP.OVERDUE;
    case 'Upcoming due':
      return POI_DUE_DATE_COLOR_MAP.UPCOMING_DUE;
    case 'Unscheduled':
      return POI_DUE_DATE_COLOR_MAP.UNSCHEDULED;
    case 'Completed':
      return POI_DUE_DATE_COLOR_MAP.COMPLETED;
    default:
      return '#E6E6E6';
  }
};

export const getAlarmLevelColors = (key: string) => {
  switch (key) {
    case 'Alarm level 1':
      return PD_ALARM_LEVEL_COLOR_MAP.ALARM_LEVEL_1;
    case 'Alarm level 2':
      return PD_ALARM_LEVEL_COLOR_MAP.ALARM_LEVEL_2;
    case 'Alarm level 3':
      return PD_ALARM_LEVEL_COLOR_MAP.ALARM_LEVEL_3;
    default:
      return '#E6E6E6';
  }
};

export const WidgetsContainer = muiStyled(Stack)({
  overflowX: 'auto',
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  cursor: 'grabbing',
  marginLeft: '0.8rem',
  paddingTop: '1.6rem',
  paddingBottom: '1.6rem',
  width: '100%',
});
